import React from 'react'
import {toast} from 'react-toastify'
import {useAuthClient} from '../../utils/use-auth-client'
import {OOE_ROLES} from '../../utils/constants'
import {
  FormOverlay,
  FormCard,
  FormTitle,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormActions,
  PrimaryButton,
  SecondaryButton,
  ErrorMessage,
  AutocompleteWrapper,
  SuggestionsList,
  SuggestionItem,
  ChipContainer,
  Chip,
  ChipRemoveButton,
  EntityTypeToggle,
  EntityTypeOption,
} from '../../screens/features.styled'

function getDominantEntityType(assignments) {
  if (!assignments || assignments.length === 0) return 'Role'
  const roleCount = assignments.filter(a => a.entityType === 'Role').length
  const userCount = assignments.filter(a => a.entityType === 'User').length
  return userCount > roleCount ? 'User' : 'Role'
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = React.useState(value)
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

function AssignmentForm({
  initialFeatureDesc,
  isDescReadOnly,
  initialAssignments = [],
  onSuccess,
  onCancel,
}) {
  const client = useAuthClient()
  const [featureDesc, setFeatureDesc] = React.useState(initialFeatureDesc)
  const [entityType, setEntityType] = React.useState(() =>
    getDominantEntityType(initialAssignments),
  )
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  // Role state — single select
  const [selectedRole, setSelectedRole] = React.useState('')

  // User state — autocomplete + chips
  const [userSearch, setUserSearch] = React.useState('')
  const [suggestions, setSuggestions] = React.useState([])
  const [isFetching, setIsFetching] = React.useState(false)
  const [selectedUsers, setSelectedUsers] = React.useState([])
  // selectedUsers: [{ displayName: string, mails: string[] }]

  const debouncedSearch = useDebounce(userSearch, 300)

  React.useEffect(() => {
    if (entityType !== 'User') return
    if (debouncedSearch.trim().length < 2) {
      setSuggestions([])
      return
    }
    let cancelled = false
    setIsFetching(true)
    client(
      `azuredirectory/search-users?q=${encodeURIComponent(
        debouncedSearch.trim(),
      )}`,
    )
      .then(results => {
        if (!cancelled) setSuggestions(results)
      })
      .catch(() => {
        if (!cancelled) setSuggestions([])
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedSearch, entityType, client])

  const handleSelectUser = user => {
    const mails = [user.mail, user.userPrincipalName]
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i)

    const alreadyAdded = selectedUsers.some(u =>
      u.mails.some(m => mails.includes(m)),
    )
    if (!alreadyAdded) {
      setSelectedUsers(prev => [
        ...prev,
        {displayName: user.displayName, mails},
      ])
    }
    setUserSearch('')
    setSuggestions([])
  }

  const handleRemoveUser = index => {
    setSelectedUsers(prev => prev.filter((_, i) => i !== index))
  }

  const handleTypeChange = e => {
    setEntityType(e.target.value)
    setSelectedRole('')
    setSelectedUsers([])
    setUserSearch('')
    setSuggestions([])
    setError(null)
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setError(null)

    const trimmedDesc = featureDesc.trim()

    let entityValue
    if (entityType === 'Role') {
      if (!selectedRole) {
        setError('Please select a role.')
        return
      }
      entityValue = [selectedRole]
    } else {
      if (selectedUsers.length === 0) {
        setError('Please add at least one user.')
        return
      }
      entityValue = selectedUsers.flatMap(u => u.mails)
    }

    setIsLoading(true)
    try {
      await client('feature-flags', {
        data: {featureDesc: trimmedDesc, entityType, entityValue},
      })
      toast.success('Rule created successfully.')
      onSuccess(trimmedDesc)
    } catch (err) {
      if (err.status === 409) {
        setError('All selected values already exist for this feature.')
      } else {
        setError(err.message || 'Something went wrong. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormOverlay onClick={onCancel}>
      <FormCard onClick={e => e.stopPropagation()}>
        <FormTitle>{isDescReadOnly ? 'Add Rule' : 'New Feature'}</FormTitle>

        <form
          onSubmit={handleSubmit}
          style={{display: 'flex', flexDirection: 'column', gap: '14px'}}
        >
          <FormGroup>
            <FormLabel htmlFor="featureDesc">Feature Name</FormLabel>
            <FormInput
              id="featureDesc"
              type="text"
              value={featureDesc}
              disabled={isDescReadOnly}
              onChange={e => setFeatureDesc(e.target.value)}
              placeholder="e.g. My_New_Feature"
              required
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Type</FormLabel>
            <EntityTypeToggle>
              <EntityTypeOption isSelected={entityType === 'Role'}>
                <input
                  type="radio"
                  name="entityType"
                  value="Role"
                  checked={entityType === 'Role'}
                  onChange={handleTypeChange}
                />
                Role
              </EntityTypeOption>
              <EntityTypeOption isSelected={entityType === 'User'}>
                <input
                  type="radio"
                  name="entityType"
                  value="User"
                  checked={entityType === 'User'}
                  onChange={handleTypeChange}
                />
                User
              </EntityTypeOption>
            </EntityTypeToggle>
          </FormGroup>

          {entityType === 'Role' ? (
            <FormGroup>
              <FormSelect
                id="roleValue"
                value={selectedRole}
                onChange={e => setSelectedRole(e.target.value)}
              >
                <option value="">Select a role...</option>
                {OOE_ROLES.map(role => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </FormSelect>
            </FormGroup>
          ) : (
            <FormGroup>
              <AutocompleteWrapper>
                <FormInput
                  id="userSearch"
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search by name..."
                  autoComplete="off"
                />
                {suggestions.length > 0 && (
                  <SuggestionsList>
                    {suggestions.map((u, i) => (
                      <SuggestionItem
                        key={i}
                        onClick={() => handleSelectUser(u)}
                      >
                        {u.displayName}
                        <span>{u.mail ?? u.userPrincipalName}</span>
                      </SuggestionItem>
                    ))}
                  </SuggestionsList>
                )}
                {isFetching &&
                  userSearch.trim().length >= 2 &&
                  suggestions.length === 0 && (
                    <SuggestionsList>
                      <SuggestionItem
                        style={{color: '#6c757d', cursor: 'default'}}
                      >
                        Searching...
                      </SuggestionItem>
                    </SuggestionsList>
                  )}
              </AutocompleteWrapper>

              {selectedUsers.length > 0 && (
                <ChipContainer>
                  {selectedUsers.map((u, i) => (
                    <Chip key={i}>
                      {u.displayName}
                      <ChipRemoveButton
                        type="button"
                        onClick={() => handleRemoveUser(i)}
                        aria-label={`Remove ${u.displayName}`}
                      >
                        ×
                      </ChipRemoveButton>
                    </Chip>
                  ))}
                </ChipContainer>
              )}
            </FormGroup>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormActions>
            <SecondaryButton
              type="button"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Create'}
            </PrimaryButton>
          </FormActions>
        </form>
      </FormCard>
    </FormOverlay>
  )
}

export {AssignmentForm}
